import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { keys } from '@/lib/queryKeys';
import type { OpdFeeItem, OpdFeeItemInput, OpdFeeOverride, OpdFeeQuote } from './types';

// Admin (admin:billing-settings)
export const getOpdFeeItems = () => api.get<OpdFeeItem[]>('/opd-fees');
export const createOpdFeeItem = (data: OpdFeeItemInput) => api.post<OpdFeeItem>('/opd-fees', data);
export const updateOpdFeeItem = (id: number, data: OpdFeeItemInput) => api.patch<OpdFeeItem>(`/opd-fees/${id}`, data);
export const addOpdFeeOverride = (itemId: number, data: { branch_id: number | null; specialty_id: number | null; fee: number }) =>
  api.post<OpdFeeOverride>(`/opd-fees/${itemId}/fees`, data);
export const updateOpdFeeOverride = (feeId: number, data: { fee?: number; active?: boolean; branch_id?: number | null; specialty_id?: number | null }) =>
  api.patch<OpdFeeOverride>(`/opd-fees/fees/${feeId}`, data);
export const deleteOpdFeeOverride = (feeId: number) => api.delete<{ deleted: boolean }>(`/opd-fees/fees/${feeId}`);

// The bill forms (billing or visit)
export interface OpdFeeQuoteParams {
  visitId?: number | null;
  specialtyId?: number | null;
  newPatient?: boolean;
}

export const getOpdFeeQuote = ({ visitId, specialtyId, newPatient }: OpdFeeQuoteParams) =>
  api.get<OpdFeeQuote>('/opd-fees/quote', {
    query: {
      visit_id: visitId ?? undefined,
      specialty_id: specialtyId ?? undefined,
      new_patient: newPatient === undefined ? undefined : String(newPatient),
    },
  });

/** The fee schedule priced for one bill — refetched when the branch/department/patient type changes. */
export function useOpdFeeQuote(params: OpdFeeQuoteParams, enabled = true) {
  return useQuery({
    queryKey: keys.opdFees.quote({ visitId: params.visitId ?? null, specialtyId: params.specialtyId ?? null, newPatient: params.newPatient ?? null }),
    queryFn: () => getOpdFeeQuote(params),
    enabled,
    staleTime: 30_000,
  });
}
